import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Api from "../utils/api";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import enrollmentFormTranslations from "../locales/enrollmentFormTranslations";
import { LanguageContext } from "../locales/LanguageContext";

import "./EnrollmentForm.css";

const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const phoneRegex = /^\+\d{8,18}$/;

const initialForm = {
  learnerName: "",
  email: "",
  phone: "",
  fatherName: "",
  address: "",
  preferredLevelId: "",
  optionalClassTypeId: "",
  consentAccepted: false,
  countryCurrencyId: "",
  currency: "",
};

const EnrollmentForm = ({
  endpoint,
  extraFields = {}, 
  showModalInfo,
  showSuccessMessage,
  submitButtonLabel = "Submit Enrollment",
}) => {
  const navigate = useNavigate();
  const { lang } = useContext(LanguageContext);
  const t = enrollmentFormTranslations[lang] || {};
  const [form, setForm] = useState(initialForm);
  const [countryList, setCountryList] = useState([]);
  const [levels, setLevels] = useState([]);
  const [optionalClasses, setOptionalClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [isEmailUnique, setIsEmailUnique] = useState(true);
  const [isPhoneUnique, setIsPhoneUnique] = useState(true);
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [phoneCountry, setPhoneCountry] = useState("in");

  useEffect(() => {
    Api.get("countrycurrency").then((data) => setCountryList(data));
    Api.get("levels").then((levels) =>
      setLevels(levels.map((l) => ({ ...l, id: l.id }))),
    );
    Api.get("optional-classes").then((classes) =>
      setOptionalClasses(classes.map((c) => ({ ...c, id: c.id }))),
    );
  }, []);

  const handleCountryChange = (e) => {
    const countryCurrencyId = e.target.value;
    const selected = countryList.find(
      (c) => c.id === Number(countryCurrencyId),
    );
    setForm({
      ...form,
      countryCurrencyId,
      currency: selected ? selected.code : "",
    });
    // Set phone input country if available
    if (selected && selected.country) {
      // Map country name to country code for react-phone-input-2
      // You may need to adjust this mapping if country names differ from codes
      const countryNameToCode = {
        India: "in",
        Australia: "au",
        Canada: "ca",
        USA: "us",
        "United States": "us",
      };
      const code = countryNameToCode[selected.country] || "in";
      setPhoneCountry(code);
    }
  };

  const checkEmailUnique = async (email) => {
    if (!email) {
      setEmailError("");
      return;
    }
    if (!emailRegex.test(email)) {
      setEmailError(t.emailInvalid);
      setSnackbar({
        open: true,
        message: t.emailInvalid,
        severity: "warning",
      });
      setIsEmailUnique(true);
      return;
    }
    try {
      const res = await Api.post("checkunique/learner-email", { email });
      if (res.exists) {
        setEmailError(t.emailExists);
        setSnackbar({
          open: true,
          message: t.emailExists,
          severity: "warning",
        });
        setIsEmailUnique(false);
      } else {
        setEmailError("");
        setIsEmailUnique(true);
      }
    } catch (err) {
      setEmailError(t.emailCheckError);
      setSnackbar({
        open: true,
        message: t.emailCheckError,
        severity: "error",
      });
      setIsEmailUnique(true);
    }
  };

  const checkPhoneUnique = async (phone) => {
    if (!phone) {
      setPhoneError("");
      return;
    }
    if (!phoneRegex.test(phone)) {
      setPhoneError(t.phoneInvalid);
      setSnackbar({
        open: true,
        message: t.phoneInvalid,
        severity: "warning",
      });
      setIsPhoneUnique(true);
      return;
    }
    try {
      const res = await Api.post("checkunique/learner-phone", { phone });
      if (res.exists) {
        setPhoneError(t.phoneExists);
        setSnackbar({
          open: true,
          message: t.phoneExists,
          severity: "warning",
        });
        setIsPhoneUnique(false);
      } else {
        setPhoneError("");
        setIsPhoneUnique(true);
      }
    } catch (err) {
      setPhoneError(t.phoneCheckError);
      setSnackbar({
        open: true,
        message: t.phoneCheckError,
        severity: "error",
      });
      setIsPhoneUnique(true);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    setForm({ ...form, [name]: newValue });
    if (name === "email") checkEmailUnique(newValue);
    if (name === "phone") checkPhoneUnique(newValue);
  };

  const handlePhoneChange = (phone) => {
    setForm({ ...form, phone: phone.startsWith("+") ? phone : "+" + phone });
    checkPhoneUnique(phone.startsWith("+") ? phone : "+" + phone);
  };

  const isFormValid =
    form.learnerName &&
    form.email &&
    emailRegex.test(form.email) &&
    isEmailUnique &&
    form.phone &&
    phoneRegex.test(form.phone) &&
    isPhoneUnique &&
    form.preferredLevelId &&
    form.consentAccepted &&
    form.fatherName &&
    form.address &&
    form.countryCurrencyId;

  const handleSubmit = async (e) => {
    if (!e.target.checkValidity()) return;
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const submitForm = { ...form, ...extraFields };
      // Convert optionalClassTypeId to null if empty string
      if (submitForm.optionalClassTypeId === "") {
        submitForm.optionalClassTypeId = null;
      }
      // Remove currency if not needed by backend
      if (!submitForm.currency) delete submitForm.currency;
      const res = await Api.post(endpoint, submitForm);
      // if (onSuccess) {
      //   onSuccess(res);
      // } else
        console.log("Enrollment successful:", res);
      if (res && res.success) {
        let response = res.data; // Capture token if returned
        // If backend returns enrollment id and token, redirect to progress page
        let url = `/enrollment-progress/${response.id}`;
        if (response.token) url += `?token=${encodeURIComponent(response.token)}`;
        navigate(url);
      }
      // else if (showSuccessMessage) {
      //   setSuccess(t.successTitle);
      // }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {showModalInfo && showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.3)",
            zIndex: 11000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg, #f8fafc 0%, #e0e7ef 100%)",
              borderRadius: 18,
              maxWidth: 420,
              width: "92%",
              padding: 36,
              boxShadow: "0 4px 32px rgba(0,0,0,0.18)",
              border: "1.5px solid #e3e8ee",
              position: "relative",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 38, marginBottom: 10, color: "#2b6cb0" }}>
              🎓
            </div>
            <h2
              style={{
                fontWeight: 800,
                marginBottom: 18,
                color: "#1a365d",
                letterSpacing: 0.5,
              }}
            >
              {t.modalTitle}
            </h2>
            <ol
              style={{
                marginBottom: 20,
                paddingLeft: 0,
                listStyle: "none",
                textAlign: "left",
                maxWidth: 320,
                margin: "0 auto 20px auto",
              }}
            >
              {(t.modalSteps || []).map((step, idx) => (
                <li
                  key={idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 10,
                  }}
                >
                  <span
                    style={{
                      fontSize: 20,
                      marginRight: 10,
                      color: [
                        "#38a169",
                        "#3182ce",
                        "#805ad5",
                        "#d69e2e",
                        "#e53e3e",
                      ][idx],
                    }}
                  >
                    {idx + 1}.
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
            <div
              style={{
                color: "#b00",
                fontWeight: 600,
                marginBottom: 18,
                background: "#fff5f5",
                borderRadius: 8,
                padding: "10px 12px",
                fontSize: 15,
                border: "1px solid #feb2b2",
                boxShadow: "0 1px 4px rgba(255,0,0,0.04)",
              }}
            >
              <span style={{ marginRight: 6 }}>🌏</span>
              {t.modalNote}
            </div>
            <button
              className="btn btn-primary w-100"
              style={{
                fontWeight: 700,
                fontSize: 18,
                padding: "10px 0",
                borderRadius: 8,
                letterSpacing: 0.5,
              }}
              onClick={() => setShowModal(false)}
            >
              {t.modalBtn}
            </button>
          </div>
        </div>
      )}
      {success && showSuccessMessage ? (
        <div
          style={{
            width: "100%",
            background: "linear-gradient(90deg, #f6815e 0%, #f6b93b 100%)",
            color: "#fff",
            padding: "38px 0 24px 0",
            textAlign: "center",
            marginBottom: 32,
            boxShadow: "0 4px 24px #f6815e22",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div style={{ fontSize: 60, marginBottom: 18 }}>🎉</div>
          <h1
            style={{
              fontWeight: 900,
              fontSize: "2.6rem",
              letterSpacing: 1,
              marginBottom: 18,
              textShadow: "0 2px 16px #38f9d799",
            }}
          >
            {t.successTitle}
          </h1>
          <div style={{ fontSize: 22, fontWeight: 600, marginBottom: 10 }}>
            {t.successSubtitle}
          </div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 400,
              maxWidth: 540,
              margin: "0 auto",
              color: "#e6fff9",
            }}
          >
            {t.successDesc}
          </div>
        </div>
      ) : null}
      <div
        className="container"
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {!success || !showSuccessMessage ? (
          <form
            onSubmit={handleSubmit}
            className="bg-white p-4 rounded shadow-sm enrollment-form-responsive"
            style={{ maxWidth: 800, width: "100%", margin: "0 auto" }}
          >
            <div
              className="enrollment-form-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 16,
                marginBottom: 16,
              }}
            >
              {/* Learner Name */}
              <div className="mb-3">
                <label className="form-label" htmlFor="learnerName">
                  {t.learnerName}
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="learnerName"
                  name="learnerName"
                  value={form.learnerName || ""}
                  onChange={handleChange}
                  required
                  placeholder={t.learnerNamePlaceholder}
                />
              </div>
              {/* Email */}
              <div className="mb-3">
                <label className="form-label" htmlFor="email">
                  {t.email}
                </label>
                <input
                  type="email"
                  className="form-control"
                  id="email"
                  name="email"
                  value={form.email || ""}
                  onChange={handleChange}
                  required
                  placeholder={t.emailPlaceholder}
                />
                {emailError && (
                  <div style={{ color: "#d32f2f", fontSize: 13, marginTop: 2 }}>
                    {emailError}
                  </div>
                )}
              </div>
              {/* Father's Name */}
              <div className="mb-3">
                <label className="form-label" htmlFor="fatherName">
                  {t.fatherName}
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="fatherName"
                  name="fatherName"
                  value={form.fatherName || ""}
                  onChange={handleChange}
                  required
                  placeholder={t.fatherNamePlaceholder}
                />
              </div>
              {/* Level */}
              <div className="mb-3">
                <label className="form-label">{t.level}</label>
                <select
                  className="form-control"
                  name="preferredLevelId"
                  value={form.preferredLevelId}
                  onChange={handleChange}
                  required
                >
                  <option value="">{t.selectLevel}</option>
                  {levels.map((lvl) => (
                    <option key={lvl.id} value={lvl.id}>
                      {lvl.title || lvl.name}
                    </option>
                  ))}
                </select>
              </div>
              {/* Country */}
              <div className="mb-3">
                <label className="form-label" htmlFor="countryCurrencyId">
                  {t.country}
                </label>
                <select
                  className="form-control"
                  id="countryCurrencyId"
                  name="countryCurrencyId"
                  value={form.countryCurrencyId}
                  onChange={handleCountryChange}
                  required
                >
                  <option value="">{t.selectCountry}</option>
                  {countryList.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.country}
                    </option>
                  ))}
                </select>
              </div>
              {/* Phone */}
              <div className="mb-3">
                <label className="form-label" htmlFor="phone">
                  {t.phone}
                </label>
                <PhoneInput
                  country={phoneCountry}
                  value={form.phone || ""}
                  onChange={handlePhoneChange}
                  inputProps={{
                    name: "phone",
                    required: true,
                    autoFocus: false,
                    id: "phone",
                  }}
                  enableSearch
                  placeholder={t.phonePlaceholder}
                  containerClass="w-100"
                  inputClass="form-control"
                  onlyCountries={["au", "ca", "in", "us"]}
                  disableCountryCode={false}
                  disableDropdown={true}
                />
                {phoneError && (
                  <div style={{ color: "#d32f2f", fontSize: 13, marginTop: 2 }}>
                    {phoneError}
                  </div>
                )}
              </div>

              {/* Optional Class */}
              <div className="mb-3">
                <label className="form-label">{t.optionalClass}</label>
                <select
                  className="form-control"
                  name="optionalClassTypeId"
                  value={form.optionalClassTypeId}
                  onChange={handleChange}
                >
                  <option value="">{t.selectOptionalClass}</option>
                  {optionalClasses.map((oc) => (
                    <option key={oc.id} value={oc.id}>
                      {oc.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Currency (auto-filled, read-only) */}
            <div className="mb-3" style={{ display: "none" }}>
              <label className="form-label" htmlFor="currency">
                Currency
              </label>
              <input
                type="text"
                className="form-control"
                id="currency"
                name="currency"
                value={form.currency || ""}
                readOnly
                placeholder="Currency will auto-fill"
              />
            </div>
            {/* Address */}
            <div className="mb-3">
              <label className="form-label" htmlFor="address">
                {t.address}
              </label>
              <input
                type="text"
                className="form-control"
                id="address"
                name="address"
                value={form.address || ""}
                onChange={handleChange}
                required
                placeholder={t.addressPlaceholder}
              />
            </div>

            {/* Consent */}
            <div
              className="mb-3"
              style={{
                border: "1px solid #eee",
                borderRadius: 8,
                padding: 16,
                background: "#f9f9f9",
              }}
            >
              <h5 style={{ fontWeight: 700, marginBottom: 8 }}>
                {t.consentTitle}
              </h5>
              <div style={{ fontWeight: 500, marginBottom: 8 }}>
                {t.consentDesc}
              </div>
              <ul style={{ marginBottom: 12, paddingLeft: 20 }}>
                {(t.consentPoints || []).map((point, idx) => (
                  <li key={idx}>{point}</li>
                ))}
              </ul>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="consentAccepted"
                  checked={form.consentAccepted}
                  onChange={handleChange}
                  required
                  id="consentCheckbox"
                />
                <label className="form-check-label" htmlFor="consentCheckbox">
                  {t.consentLabel}
                </label>
              </div>
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!isFormValid || loading}
            >
              {loading ? t.submitting : submitButtonLabel || t.submitBtn}
            </button>
          </form>
        ) : null}
        {error && <div className="alert alert-danger">{error}</div>}
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EnrollmentForm;
